import clsx from "clsx";
import { usePagedTransactionsQuery, useDocumentColumns } from "./hooks";
import {
  Table,
  PaginationContextProvider,
  Pagination,
  PaginationInfo,
  TextInput,
  SearchIcon,
  Loading,
} from "@landtechnologies/components";
import { useTranslation } from "react-migration/lib";
import { useRef, useState } from "react";
import { router } from "src/js/router/react-router";
import { useEventListener } from "usehooks-ts";
import { NoSearchResults } from "./components";

const PER_PAGE = 15;

export const Documents = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const pageNrQuery = searchParams.get("page");
  const [searchValue, setSearchValue] = useState(searchParams.get("search") || "");
  const [searchValueQuery, setSearchValueQuery] = useState(searchParams.get("search") || "");

  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(() => {
    if (Number(pageNrQuery) - 1 > 0) {
      return Number(pageNrQuery) - 1;
    }
    return 0;
  });
  const { data, error, loading } = usePagedTransactionsQuery({
    page: currentPage,
    perPage: PER_PAGE,
    search: searchValueQuery,
  });

  type queryParamsType = { page: string; search?: string };

  const pagination = data?.pagedTransactions.pagination;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const documentRef = useRef(document);
  useEventListener(
    "keyup",
    (e) => {
      if (e.key === "Enter") {
        setSearchValueQuery(searchValue);
        const queryParams = { page: String(currentPage + 1) } as queryParamsType;
        if (searchValue) queryParams.search = searchValue;
        router.push({ query: queryParams });
      }
    },
    documentRef
  );

  const columns = useDocumentColumns();
  return (
    <div className="documents-tab atlas-flex-grow atlas-min-w-[1024px] ">
      <div className="atlas-py-[10px]">
        <h2 className="atlas-text-sm atlas-font-medium sm:atlas-text-3xl atlas-mt-5 atlas-mb-2 atlas-py-[10px]">
          {t("dashboard.transactions.purchased_documents")}
        </h2>
      </div>
      <div
        data-testid="documents-table"
        className={clsx("atlas-h-full", "atlas-w-full", "atlas-bg-background-white")}
      >
        {loading && <Loading />}
        {error ? (
          <div>{t("dashboard.transactions.error")}</div>
        ) : (
          <>
            {((data?.pagedTransactions?.nodes?.length && !!pagination) ||
              (searchValueQuery && !!pagination)) && (
              <PaginationContextProvider
                currentPage={pagination.currentPage}
                itemsPerPage={pagination.perPage}
                total={pagination.totalCount}
                pageCount={pagination.totalPages}
                onPageChange={handlePageChange}
              >
                <div className="atlas-mb-4 atlas-flex atlas-justify-between atlas-items-end">
                  <PaginationInfo />
                  <TextInput
                    value={searchValue}
                    IconLeft={
                      <span className="atlas-flex atlas-text-neutral-400 atlas-w-4">
                        <SearchIcon />
                      </span>
                    }
                    placeholder={t("dashboard.transactions.search_for_document")}
                    onChange={handleSearch}
                  />
                </div>
                {data?.pagedTransactions?.nodes?.length > 0 && (
                  <div className={pagination.totalPages < 2 ? "atlas-pb-[40px]" : ""}>
                    <Table
                      primaryKey="created"
                      columns={columns}
                      rows={data.pagedTransactions.nodes}
                    />
                  </div>
                )}

                {pagination.totalPages > 1 && (
                  <div className="atlas-flex atlas-justify-center atlas-py-6">
                    <Pagination />
                  </div>
                )}
              </PaginationContextProvider>
            )}
          </>
        )}
        {searchValueQuery
          ? data?.pagedTransactions?.nodes?.length === 0 && <NoSearchResults />
          : data?.pagedTransactions?.nodes?.length === 0 && (
              <div>{t("dashboard.transactions.no_purchased_documents")}</div>
            )}
      </div>
    </div>
  );
};
